import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// The Single Social Club pin+heart mark, rendered as a white neon glow, that
// splits cleanly down the middle and opens like doors on first landing.
// Vector pin + heart-cutout + ripples — crisp at any size / device-pixel-ratio.
function Mark() {
    return (
        <svg
            className="gate-mark"
            viewBox="0 0 200 270"
            aria-hidden="true"
            style={{ width: "min(62vw, 340px)", height: "auto" }}
        >
            {/* pin body with heart cut out (evenodd hole) */}
            <path
                fillRule="evenodd"
                fill="#ffffff"
                d="M100 214 C70 150 42 120 42 84 A58 58 0 1 1 158 84 C158 120 130 150 100 214 Z
                   M100 116 C80 98 64 86 64 73 C64 63 72 56 81 56 C89 56 96 61 100 69 C104 61 111 56 119 56 C128 56 136 63 136 73 C136 86 120 98 100 116 Z"
            />
            {/* ripple rings under the pin */}
            <g fill="none" stroke="#ffffff" strokeLinecap="round">
                <ellipse cx="100" cy="238" rx="52" ry="13" strokeWidth="7" />
                <ellipse cx="100" cy="238" rx="30" ry="7.5" strokeWidth="6" />
            </g>
        </svg>
    );
}

// Each half renders the FULL mark centered exactly on the 50% seam; the parent
// panel's overflow-hidden clips it to the correct half. This guarantees the
// assembled mark is perfectly centered on the viewport.
function MarkHalf({ side }) {
    const isLeft = side === "left";
    return (
        <div
            className="absolute top-1/2"
            style={{
                [isLeft ? "right" : "left"]: 0,
                transform: `translateY(-50%) translateX(${isLeft ? "50%" : "-50%"})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Mark />
        </div>
    );
}

export default function HeartGateIntro() {
    const [open, setOpen] = useState(false);
    const [done, setDone] = useState(false);

    const finish = () => {
        setDone(true);
        try { window.dispatchEvent(new Event("ssc-intro-done")); } catch (e) {}
    };

    useEffect(() => {
        const t1 = setTimeout(() => setOpen(true), 1400);
        const fallback = setTimeout(() => finish(), 7200); // safety net
        return () => {
            clearTimeout(t1);
            clearTimeout(fallback);
        };
    }, []);

    if (done) return null;

    const ease = [0.83, 0, 0.17, 1];
    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[200] overflow-hidden" data-testid="heart-gate-intro">
                <motion.div
                    className="absolute left-0 top-0 h-full w-1/2 overflow-hidden bg-black"
                    initial={{ x: 0 }}
                    animate={{ x: open ? "-101%" : 0 }}
                    transition={{ duration: 4.8, ease }}
                    onAnimationComplete={() => { if (open) finish(); }}
                >
                    <MarkHalf side="left" />
                </motion.div>
                <motion.div
                    className="absolute right-0 top-0 h-full w-1/2 overflow-hidden bg-black"
                    initial={{ x: 0 }}
                    animate={{ x: open ? "101%" : 0 }}
                    transition={{ duration: 4.8, ease }}
                >
                    <MarkHalf side="right" />
                </motion.div>
                {/* faint center seam glow while closed */}
                <motion.div
                    className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2"
                    style={{ background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.65), transparent)" }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: open ? 0 : 1 }}
                    transition={{ duration: 0.6 }}
                />
            </div>
        </AnimatePresence>
    );
}
